import React, { ElementType } from 'react'
import { SubmitError, SubmitErrorType } from '@typesApp/SubmitError'
import { injectDataAttributes } from '@utils/dataAttributes'
import { useAppTranslation } from '@hooks/useAppTranslation'
import { Button } from '@components/ui-css/Button/Button'
import { ButtonLink } from '@components/ui-css/ButtonLink/ButtonLink'
import { Modal, ModalActionContainer, ModalContent, ModalFooter, ModalHeader } from '@components/ui-css/Modal/Modal'
import styles from '../styles/SubmitErrorListener.module.scss'

type QuoteErrorModalProps = {
  onBackToForm: () => void
  onProceedToCheckout: () => void
  error: SubmitError
}

export const QuoteErrorModal: React.FC<QuoteErrorModalProps> = props => {
  const { onBackToForm, onProceedToCheckout, error } = props

  const modals = {
    REQUEST_QUOTE_ERROR: RequestQuoteErrorModal,
    REQUEST_QUOTE_RESTRICTED: QuoteRestrictedModal,
    SUBMIT_QUOTE_ERROR: SubmitQuoteErrorModal,
    SUBMIT_QUOTE_NO_RETRY_ERROR: SubmitQuoteNoRetryErrorModal,
    INSUFFICIENT_COVERAGE: InsufficientCoverageErrorModal,
  } as Record<SubmitErrorType, ElementType<QuoteErrorModalProps>>

  const DynamicErrorModal = modals[error.type]
  return DynamicErrorModal ? (
    <DynamicErrorModal onBackToForm={onBackToForm} onProceedToCheckout={onProceedToCheckout} error={error} />
  ) : null
}

const RequestQuoteErrorModal: React.FC<QuoteErrorModalProps> = props => {
  const { onBackToForm, onProceedToCheckout, error } = props
  const { t } = useAppTranslation({ keyPrefix: 'SubmitError.requestQuote' })

  return (
    <Modal open={true} onClose={onBackToForm}>
      <ModalHeader>{t('title')}</ModalHeader>
      <ModalContent>
        <span className={styles.errorMessage} dangerouslySetInnerHTML={{ __html: t('message') }}></span>
        {error?.details && <span className={styles.errorMessageDetails}>{error.details}</span>}
      </ModalContent>
      <ModalFooter>
        <ModalActionContainer>
          <ButtonLink underline onClick={onBackToForm} {...injectDataAttributes('modal.backToForm')}>
            {t('back-cta')}
          </ButtonLink>
          <Button onClick={onProceedToCheckout} {...injectDataAttributes('modal.proceedToCheckout')}>
            {t('proceed-cta')}
          </Button>
        </ModalActionContainer>
      </ModalFooter>
    </Modal>
  )
}

const SubmitQuoteNoRetryErrorModal: React.FC<QuoteErrorModalProps> = props => {
  const { onProceedToCheckout } = props
  const { t } = useAppTranslation({ keyPrefix: 'SubmitError.submitQuote' })

  return (
    <Modal open={true} onClose={onProceedToCheckout}>
      <ModalHeader>{t('title')}</ModalHeader>
      <ModalContent>
        <span className={styles.errorMessage} dangerouslySetInnerHTML={{ __html: t('message') }}></span>
      </ModalContent>
      <ModalFooter>
        <ModalActionContainer>
          <Button onClick={onProceedToCheckout} {...injectDataAttributes('modal.proceedToCheckout')}>
            {t('proceed-cta')}
          </Button>
        </ModalActionContainer>
      </ModalFooter>
    </Modal>
  )
}

const SubmitQuoteErrorModal: React.FC<QuoteErrorModalProps> = props => {
  const { onBackToForm, onProceedToCheckout, error } = props
  const { t } = useAppTranslation({ keyPrefix: 'SubmitError.submitQuote' })

  return (
    <Modal open={true} onClose={onProceedToCheckout}>
      <ModalHeader>{t('title')}</ModalHeader>
      <ModalContent>
        <span className={styles.errorMessage} dangerouslySetInnerHTML={{ __html: t('message') }}></span>
        {error?.details && <span className={styles.errorMessageDetails}>{error.details}</span>}
      </ModalContent>
      <ModalFooter>
        <ModalActionContainer>
          <ButtonLink underline onClick={onProceedToCheckout} {...injectDataAttributes('modal.proceedToCheckout')}>
            {t('proceed-cta')}
          </ButtonLink>
          <Button onClick={onBackToForm} {...injectDataAttributes('modal.backToForm')}>
            {t('back-cta')}
          </Button>
        </ModalActionContainer>
      </ModalFooter>
    </Modal>
  )
}

const InsufficientCoverageErrorModal: React.FC<QuoteErrorModalProps> = props => {
  const { onProceedToCheckout, error } = props
  const { t } = useAppTranslation({ keyPrefix: 'SubmitError.insufficientCoverage' })

  return (
    <Modal open={true} onClose={onProceedToCheckout}>
      <ModalHeader>{t('title')}</ModalHeader>
      <ModalContent>
        <span className={styles.errorMessage} dangerouslySetInnerHTML={{ __html: t('message') }}></span>
        {error?.details && <span className={styles.errorMessageDetails}>{error.details}</span>}
      </ModalContent>
      <ModalFooter>
        <ModalActionContainer>
          <Button onClick={onProceedToCheckout} {...injectDataAttributes('modal.proceedToCheckout')}>
            {t('proceed-cta')}
          </Button>
        </ModalActionContainer>
      </ModalFooter>
    </Modal>
  )
}

const QuoteRestrictedModal: React.FC<QuoteErrorModalProps> = props => {
  const { onProceedToCheckout } = props
  const { t } = useAppTranslation({ keyPrefix: 'SubmitError.quoteRestricted' })

  return (
    <Modal open={true} onClose={onProceedToCheckout}>
      <ModalHeader>{t('title')}</ModalHeader>
      <ModalContent>
        <span className={styles.errorMessage} dangerouslySetInnerHTML={{ __html: t('message') }}></span>
      </ModalContent>
      <ModalFooter>
        <ModalActionContainer>
          <Button onClick={onProceedToCheckout} {...injectDataAttributes('modal.ok')}>
            {t('ok-cta')}
          </Button>
        </ModalActionContainer>
      </ModalFooter>
    </Modal>
  )
}
